<template>
  <div class="modern-card">
    <div class="card-header">
      <h2 class="card-title text-center mb-0">Stimmungsverlauf</h2>
    </div>

    <div class="card-content">
      <div v-if="!hasLoaded" class="text-center">
        <button @click="loadData" :disabled="isLoading" class="modern-btn">
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Stimmung im Saisonverlauf
        </button>
      </div>

      <template v-if="hasLoaded && selectedSeason">
        <!-- Saisonauswahl: Buttons ab Tablet, darunter ein Dropdown -->
        <div class="season-buttons mb-3">
          <div class="btn-group flex-wrap d-none d-md-block" role="group">
            <button v-for="seasonData in seasons" :key="seasonData.season"
                    class="modern-btn me-2 mb-2"
                    :class="{ 'modern-btn-outline': seasonData.season !== selectedSeason.season }"
                    @click="selectedSeasonName = seasonData.season">
              {{ seasonData.season }}
            </button>
          </div>

          <div class="d-md-none">
            <select class="form-select modern-select" :value="selectedSeason.season"
                    @change="selectedSeasonName = $event.target.value">
              <option v-for="seasonData in seasons" :key="seasonData.season" :value="seasonData.season">
                {{ seasonData.season }}
              </option>
            </select>
          </div>
        </div>

        <!-- Ansichts-Umschalter. Eine Ansicht zu entfernen heisst: Eintrag aus VIEWS streichen,
             Import loeschen, Datei loeschen - sonst haengt nichts daran. -->
        <div class="view-switch mb-3" role="tablist">
          <button v-for="view in views" :key="view.id" type="button" class="view-switch-btn"
                  :class="{ 'view-switch-btn-active': view.id === activeView }"
                  role="tab" :aria-selected="view.id === activeView"
                  @click="activeView = view.id">
            <span class="d-none d-sm-inline">{{ view.label }}</span>
            <span class="d-sm-none">{{ view.shortLabel }}</span>
          </button>
        </div>

        <div v-if="activeViewDefinition.showStats" class="season-stats mb-3">
          <div class="stat">
            <font-awesome-icon icon="arrow-trend-up" class="stat-icon" />
            <strong class="stat-value">{{ formatScore(selectedSeason.high.avgScore) }}</strong>
            <small class="stat-label">Hoch {{ formatDateShort(selectedSeason.high.date) }}</small>
          </div>
          <div class="stat">
            <font-awesome-icon icon="arrow-trend-down" class="stat-icon" />
            <strong class="stat-value">{{ formatScore(selectedSeason.low.avgScore) }}</strong>
            <small class="stat-label">Tief {{ formatDateShort(selectedSeason.low.date) }}</small>
          </div>
          <div class="stat">
            <strong class="stat-value">{{ selectedSeason.mostVotes.votes }}</strong>
            <small class="stat-label">Meiste Stimmen</small>
          </div>
          <div class="stat">
            <span class="stat-mark" aria-hidden="true">Ø</span>
            <strong class="stat-value">{{ formatScore(selectedSeason.avgScore) }}</strong>
            <span class="visually-hidden">Durchschnitt</span>
          </div>
        </div>

        <keep-alive>
          <component :is="activeComponent" v-bind="activeViewProps" />
        </keep-alive>

        <div v-if="selectedSeason.description" class="season-description mt-3">
          {{ selectedSeason.description }}
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { toast } from 'vue3-toastify';
import { useMoodHistory } from './useMoodHistory.js';
import { formatDateShort, formatScore } from './moodHistory.js';
import MoodTrendChart from './MoodTrendChart.vue';
import MoodMatchList from './MoodMatchList.vue';
import MoodMatchGrid from './MoodMatchGrid.vue';
import SeasonComparisonChart from './SeasonComparisonChart.vue';

/**
 * Container des Stimmungsverlaufs: laedt die Daten, haelt Saison- und Ansichtswahl und rendert
 * die gewaehlte Darstellung. Ersetzt die frühere ApexChart.vue.
 *
 * Alle Ansichten sind eigenstaendige Komponenten mit derselben Props-Signatur; welche angeboten
 * werden, steht allein im VIEWS-Array. Der Zustand ist rein reaktiv - die Vorgaengerfassung hat
 * das Chart beim Saisonwechsel imperativ ueber ApexCharts.exec() aktualisiert und dabei einen
 * Teil des Zustands nicht mitgefuehrt, sodass ein Re-Render auf die Startsaison zuruecksprang.
 */
// props() liefert genau die Props der jeweiligen Ansicht. Ein pauschales v-bind ueber alle
// Ansichten wuerde die nicht deklarierten Werte als HTML-Attribute durchreichen - das komplette
// Saison-Array landete dann serialisiert im DOM.
// shortLabel greift unter 576px - "Saisonvergleich" passt dort nicht in die Leiste.
// showStats blendet die Kennzahlenzeile ein. Sie beschreibt genau eine Saison und passt damit
// nur zum Verlauf; der Saisonvergleich braucht eine eigene Darstellung, die die ausgewaehlten
// Saisons einbezieht, und Liste wie Raster tragen ihre Zahlen bereits in den Zeilen.
const VIEWS = [
  {
    id: 'trend', label: 'Verlauf', shortLabel: 'Verlauf', component: MoodTrendChart,
    showStats: true,
    props: ctx => ({ season: ctx.selectedSeason })
  },
  {
    id: 'list', label: 'Spieltage', shortLabel: 'Liste', component: MoodMatchList,
    draft: true,
    props: ctx => ({ season: ctx.selectedSeason })
  },
  {
    id: 'grid', label: 'Raster', shortLabel: 'Raster', component: MoodMatchGrid,
    draft: true,
    props: ctx => ({ season: ctx.selectedSeason })
  },
  {
    id: 'comparison', label: 'Saisonvergleich', shortLabel: 'Vergleich', component: SeasonComparisonChart,
    props: ctx => ({ seasons: ctx.seasons, highlightedSeason: ctx.selectedSeason.season })
  }
];

/**
 * Noch nicht ausgearbeitete Ansichten (draft) erscheinen nur, wenn VUE_APP_SHOW_DRAFT_VIEWS
 * gesetzt ist. Die Variable steht in .env.development, und die laedt Vue CLI ausschliesslich beim
 * Entwicklungsserver - in jedem Produktionsbuild fehlt sie damit, ohne dass jemand daran denken
 * muss. Umgekehrt heisst das: Die blosse Existenz der Variable schaltet frei, ihr Wert ist egal.
 *
 * Gefiltert wird einmal beim Laden des Moduls, nicht je Aufruf: Die Variable wird zur Bauzeit
 * eingesetzt und kann sich zur Laufzeit nicht mehr aendern.
 */
const AVAILABLE_VIEWS = VIEWS.filter(view => !view.draft || Boolean(process.env.VUE_APP_SHOW_DRAFT_VIEWS));

export default {
  name: 'MoodHistoryCard',
  components: { MoodTrendChart, MoodMatchList, MoodMatchGrid, SeasonComparisonChart },
  setup() {
    const { seasons, isLoading, hasLoaded, load } = useMoodHistory();
    return { seasons, isLoading, hasLoaded, load };
  },
  data() {
    return {
      activeView: AVAILABLE_VIEWS[0].id,
      selectedSeasonName: null,
      views: AVAILABLE_VIEWS.map(({ id, label, shortLabel }) => ({ id, label, shortLabel }))
    };
  },
  computed: {
    selectedSeason() {
      if (this.seasons.length === 0) return null;
      return this.seasons.find(s => s.season === this.selectedSeasonName)
        || this.seasons[this.seasons.length - 1];
    },
    activeViewDefinition() {
      return AVAILABLE_VIEWS.find(v => v.id === this.activeView) || AVAILABLE_VIEWS[0];
    },
    activeComponent() {
      return this.activeViewDefinition.component;
    },
    activeViewProps() {
      return this.activeViewDefinition.props(this);
    }
  },
  methods: {
    formatDateShort,
    formatScore,
    async loadData() {
      try {
        const seasons = await this.load();
        // Standardmaessig die aktuellste Saison - sie steht in der Antwort hinten.
        if (seasons.length > 0) this.selectedSeasonName = seasons[seasons.length - 1].season;
      } catch (err) {
        console.log(err);
        toast.error('Fehler: Chart konnte nicht geladen werden. :(');
      }
    }
  },
  mounted() {
    // Hat eine andere Komponente die Daten schon geladen, direkt anzeigen statt erneut fragen.
    if (this.hasLoaded && this.seasons.length > 0 && !this.selectedSeasonName) {
      this.selectedSeasonName = this.seasons[this.seasons.length - 1].season;
      return;
    }

    // Laedt sofort beim Einhaengen statt erst auf Klick - die Karte sitzt unterhalb des Falts,
    // der Nutzer muss ohnehin erst zu ihr scrollen. Reagiert das Backend schnell genug, steht der
    // Chart schon, wenn der Bereich ins Bild kommt. Button, Spinner (isLoading) und die
    // Fehlerbehandlung in loadData() bleiben unveraendert - load() dedupliziert parallele Aufrufe
    // selbst (siehe useMoodHistory.js), ein Klick auf den waehrenddessen weiter sichtbaren Button
    // haengt sich also nur an denselben Request an, statt einen zweiten auszuloesen.
    if (!this.hasLoaded) this.loadData();
  }
};
</script>

<style scoped>
.view-switch {
  /* inline-flex statt flex: die Box umschliesst nur die Tabs, statt als Block auf voller
     Kartenbreite zu stehen - passend zu .view-switch-btn (flex: 0 0 auto). max-width faengt den
     Fall ab, dass die Tabs selbst breiter sind als die Karte (viele Ansichten auf schmalem
     Screen); overflow-x greift dann wie zuvor. */
  display: inline-flex;
  max-width: 100%;
  gap: 4px;
  padding: 4px;
  background: var(--secondary-bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius-md);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.view-switch::-webkit-scrollbar {
  display: none;
}

.view-switch-btn {
  /* Breite richtet sich nach dem Text (wie die Saison-Buttons darueber), statt sich gleichmaessig
     ueber die Box zu strecken - bei nur zwei Ansichten in Produktion (Verlauf, Saisonvergleich;
     Spieltage/Raster sind draft, siehe VIEWS in der Skript-Sektion) wirkten zwei auf 50% Breite
     aufgeblasene Tabs sonst unverhaeltnismaessig breit. */
  flex: 0 0 auto;
  white-space: nowrap;
  padding: var(--spacing-xs) var(--spacing-md);
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-secondary);
  font-family: var(--font-family);
  font-size: var(--fs-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

/* :not(...) ist noetig, weil :hover sonst die Aktiv-Klasse ueberwiegt (hoehere Spezifitaet) -
   der aktive Tab verlor seine Markierung, sobald der Zeiger darauf stand. */
.view-switch-btn:not(.view-switch-btn-active):hover {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.06);
}

.view-switch-btn-active {
  background: var(--accent-primary);
  color: var(--text-primary);
  box-shadow: var(--shadow-sm);
}

.season-stats {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs) var(--spacing-md);
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--secondary-bg);
  border-radius: var(--radius-md);
}

/* Wert und Bezeichnung stehen nebeneinander statt uebereinander - das halbiert die Hoehe der
   Zeile gegenueber der frueheren Kachelform. Ein Flex-Umbruch statt fester Spalten, damit auf
   jeder Breite so viele Kennzahlen nebeneinander stehen, wie hineinpassen. */
.stat {
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

/* Bewusst ohne tabular-nums: Die Ziffern stehen einzeln und nicht in Spalten untereinander,
   feste Ziffernbreiten liessen sie hier nur auseinanderfallen. */
.stat-value {
  font-size: var(--fs-base);
  font-weight: 600;
  line-height: 1.4;
}

/* Hoch und Tief trugen hier --success und --error. Auf dem Karten-Gradienten kommt Gruen auf
   1,21:1 und Rot auf 1,22:1 - fuer Text sind 4,5:1 gefordert, beide waren also praktisch nicht
   lesbar, und der Wert wandert mit dem Verlauf des Gradienten noch. Die Richtung tragen jetzt
   Pfeilform und Beschriftung; die Zahlen stehen wie alle anderen in der Schriftfarbe. */
.stat-icon {
  color: var(--text-secondary);
  font-size: var(--fs-sm);
}

/* Dasselbe Zeichen wie an der Durchschnittslinie im Verlaufschart, damit die Kennzahl und ihre
   Linie als dasselbe zu erkennen sind. Es steht anstelle des Wortes und spart die Breite, die
   die Zeile auf dem Handy in die dritte Reihe gedrueckt hat. */
.stat-mark {
  color: var(--text-secondary);
  font-size: var(--fs-base);
}

.stat-label {
  color: var(--text-secondary);
  font-size: var(--fs-sm);
}

.season-description {
  padding: var(--spacing-md);
  background: var(--secondary-bg);
  border-radius: var(--radius-md);
  font-size: var(--fs-sm);
}
</style>
